import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../api/errors.ts";
import type { components } from "../api/schema";
import { useApiClient } from "../session/SessionProvider.tsx";
import { devicesQueryKey } from "./devices.ts";

// Гости и invite E-va — только у её владельца. Ключи лежат под `devices…`: App WS `invite.accepted`
// инвалидирует `devices` целиком и заодно перезагружает их.

export type Guest = components["schemas"]["GuestAccessResponse"];
export type Invite = components["schemas"]["InviteResponse"];

export function guestsQueryKey(deviceId: string) {
  return [...devicesQueryKey, deviceId, "guests"] as const;
}

export function invitesQueryKey(deviceId: string) {
  return [...devicesQueryKey, deviceId, "invites"] as const;
}

/** Активные и заблокированные гости E-va. */
export function useGuests(deviceId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: guestsQueryKey(deviceId),
    queryFn: async () => {
      const { data, error, response } = await client.GET("/v1/devices/{deviceId}/guests", {
        params: { path: { deviceId } },
      });
      if (!data) throw ApiError.from(response, error);
      return data.guests;
    },
  });
}

/** Выданные invite, которые ещё можно принять. */
export function useInvites(deviceId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: invitesQueryKey(deviceId),
    queryFn: async () => {
      const { data, error, response } = await client.GET("/v1/devices/{deviceId}/invites", {
        params: { path: { deviceId } },
      });
      if (!data) throw ApiError.from(response, error);
      return data.invites;
    },
  });
}

// Ссылка уходит одному человеку через мессенджер: принять её можно один раз и в течение недели.
const INVITE_MAX_USES = 1;
export const INVITE_TTL_DAYS = 7;
const INVITE_TTL_MS = INVITE_TTL_DAYS * 24 * 60 * 60 * 1000;

/** Создать invite; токен приходит только в этом ответе — из него строится ссылка. */
export function useCreateInvite(deviceId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error, response } = await client.POST("/v1/devices/{deviceId}/invites", {
        params: { path: { deviceId } },
        body: { maxUses: INVITE_MAX_USES, expiresAt: new Date(Date.now() + INVITE_TTL_MS).toISOString() },
      });
      if (!data) throw ApiError.from(response, error);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: invitesQueryKey(deviceId) }),
  });
}

export function useRevokeInvite(deviceId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { data, error, response } = await client.DELETE("/v1/devices/{deviceId}/invites/{inviteId}", {
        params: { path: { deviceId, inviteId } },
      });
      if (!data) throw ApiError.from(response, error);
      return data;
    },
    onSuccess: (revoked) => {
      queryClient.setQueryData<Invite[]>(invitesQueryKey(deviceId), (invites) =>
        invites?.filter((item) => item.id !== revoked.id),
      );
    },
  });
}

export type GuestAction = "block" | "unblock" | "remove";

/** Заблокировать, разблокировать или удалить гостя; ответ backend'а сразу правит список. */
export function useGuestAction(deviceId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, action }: { userId: string; action: GuestAction }) => {
      const params = { path: { deviceId, userId } };
      const { data, error, response } =
        action === "remove"
          ? await client.DELETE("/v1/devices/{deviceId}/guests/{userId}", { params })
          : action === "block"
            ? await client.POST("/v1/devices/{deviceId}/guests/{userId}/block", { params })
            : await client.POST("/v1/devices/{deviceId}/guests/{userId}/unblock", { params });
      if (!data) throw ApiError.from(response, error);
      return data;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<Guest[]>(guestsQueryKey(deviceId), (guests) =>
        updated.status === "revoked"
          ? guests?.filter((item) => item.userId !== updated.userId)
          : guests?.map((item) => (item.userId === updated.userId ? updated : item)),
      );
    },
  });
}

/** Ссылка-приглашение на этот же PWA: `/invite/<token>` открывает экран принятия. */
export function inviteLink(token: string): string {
  return `${window.location.origin}/invite/${encodeURIComponent(token)}`;
}
